import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { SvgSplit } from '@actual-app/components/icons/v0';
import { SvgCheveronDown } from '@actual-app/components/icons/v1';
import { SvgArrowsSynchronize } from '@actual-app/components/icons/v2';
import { Menu } from '@actual-app/components/menu';
import type { MenuItem } from '@actual-app/components/menu';
import { Popover } from '@actual-app/components/popover';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import {
  ungroupTransactions,
  updateTransaction,
} from '@actual-app/core/shared/transactions';
import type {
  CategoryEntity,
  TransactionEntity,
} from '@actual-app/core/types/models';
import {
  format as formatDate,
  isValid as isDateValid,
  parseISO,
} from 'date-fns';

import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { FinancialText } from '#components/FinancialText';
import { Cell, Field, Row, Table, TableHeader } from '#components/table';
import { useAccounts } from '#hooks/useAccounts';
import { SchedulesProvider } from '#hooks/useCachedSchedules';
import { useCategories } from '#hooks/useCategories';
import { useCategory } from '#hooks/useCategory';
import { useCategoryScheduleGoalTemplateIndicator } from '#hooks/useCategoryScheduleGoalTemplateIndicator';
import { useDateFormat } from '#hooks/useDateFormat';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { useTransactions } from '#hooks/useTransactions';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { aqlQuery } from '#queries/aqlQuery';
import { useDispatch } from '#redux';

type CategoryActivityModalProps = Extract<
  ModalType,
  { name: 'category-activity-preview' }
>['options'];

const schedulesQuery = q('schedules').select('*');

type CategoryCellProps = {
  categoryName: string;
  menuItems: MenuItem<string>[];
  onSelect: (categoryId: CategoryEntity['id']) => void;
};

function CategoryCell({
  categoryName,
  menuItems,
  onSelect,
}: CategoryCellProps) {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        ref={triggerRef}
        variant="bare"
        style={{ maxWidth: '100%', gap: 4 }}
        onPress={() => setIsOpen(true)}
      >
        <Text
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {categoryName}
        </Text>
        <SvgCheveronDown style={{ width: 8, height: 8, flexShrink: 0 }} />
      </Button>
      <Popover
        triggerRef={triggerRef}
        placement="bottom start"
        isOpen={isOpen}
        onOpenChange={() => setIsOpen(false)}
        style={{ minWidth: 200, maxHeight: 300 }}
      >
        <Menu
          items={menuItems}
          onMenuSelect={categoryId => {
            setIsOpen(false);
            onSelect(categoryId);
          }}
        />
      </Popover>
    </>
  );
}

export function CategoryActivityModal(props: CategoryActivityModalProps) {
  // Modals render outside the budget table, so they need their own provider.
  return (
    <SchedulesProvider query={schedulesQuery}>
      <CategoryActivityModalContent {...props} />
    </SchedulesProvider>
  );
}

function CategoryActivityModalContent({
  categoryId,
  month,
}: CategoryActivityModalProps) {
  const { t } = useTranslation();
  const { data: category } = useCategory(categoryId);
  const locale = useLocale();
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const format = useFormat();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: categories = { list: [], grouped: [] } } = useCategories();

  const safeCategory = useMemo<CategoryEntity>(
    () =>
      category ?? {
        id: categoryId,
        name: '',
        is_income: false,
        group: '',
      },
    [category, categoryId],
  );

  const { schedule, scheduleStatus, description } =
    useCategoryScheduleGoalTemplateIndicator({
      category: safeCategory,
      month,
    });
  const hasSchedule = Boolean(schedule && scheduleStatus);

  const baseTransactionsQuery = useMemo(
    () =>
      q('transactions')
        .options({ splits: 'inline' })
        .filter({
          category: categoryId,
          date: { $transform: '$month', $eq: month },
        })
        .orderBy({ date: 'desc' })
        .select('*'),
    [categoryId, month],
  );

  const { transactions = [], isPending: isTransactionsLoading } =
    useTransactions({
      query: baseTransactionsQuery,
      options: { pageSize: 500 },
    });

  const totalAmount = useMemo(
    () => transactions.reduce((sum, tr) => sum + (tr.amount || 0), 0),
    [transactions],
  );

  const { data: payeesById = {} } = usePayeesById();
  const { data: accounts = [] } = useAccounts();
  const accountsById = useMemo(
    () => new Map(accounts.map(a => [a.id, a])),
    [accounts],
  );

  const getPayeeName = useCallback(
    (transaction: TransactionEntity) => {
      if (!transaction.payee) return '';
      const payee = payeesById[transaction.payee];
      if (!payee) {
        if (transaction.payee.startsWith('new:')) {
          return transaction.payee.slice(4);
        }
        return '';
      }
      if (payee.transfer_acct) {
        const transferAccount = accountsById.get(payee.transfer_acct);
        return transferAccount ? transferAccount.name : payee.name;
      }
      return payee.name;
    },
    [payeesById, accountsById],
  );

  const getAccountName = useCallback(
    (transaction: TransactionEntity) => {
      if (!transaction.account) return '';
      const account = accountsById.get(transaction.account);
      return account ? account.name : '';
    },
    [accountsById],
  );

  const formatTransactionDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) return '';
      const parsed = parseISO(dateStr);
      if (!isDateValid(parsed)) return dateStr;
      return formatDate(parsed, dateFormat);
    },
    [dateFormat],
  );

  const categoryMenuItems = useMemo<MenuItem<string>[]>(
    () =>
      categories.grouped
        .filter(group => !group.hidden)
        .flatMap(group => [
          { type: Menu.label, name: group.name, text: group.name } as const,
          ...(group.categories ?? [])
            .filter(c => !c.hidden && c.id !== categoryId)
            .map(c => ({ name: c.id, text: c.name })),
        ]),
    [categories.grouped, categoryId],
  );

  const onChangeCategory = useCallback(
    async (
      transaction: TransactionEntity,
      newCategoryId: CategoryEntity['id'],
    ) => {
      if (newCategoryId === categoryId) return;

      const { data } = await aqlQuery(
        q('transactions')
          .filter({ id: transaction.id })
          .select('*')
          .options({ splits: 'grouped' }),
      );
      const related = ungroupTransactions(data as TransactionEntity[]);
      const current = related.find(tr => tr.id === transaction.id);
      if (!current) return;

      const { diff } = updateTransaction(related, {
        ...current,
        category: newCategoryId,
      });
      await send('transactions-batch-update', diff);

      const newCategory = categories.list.find(c => c.id === newCategoryId);
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Transaction moved to {{category}}.', {
              category: newCategory?.name ?? '',
            }),
          },
        }),
      );
    },
    [categoryId, categories.list, dispatch, t],
  );

  const formattedMonth = monthUtils.format(month, 'MMMM yyyy', locale);

  const onOpenFullActivity = useCallback(() => {
    const filterConditions = [
      { field: 'category', op: 'is', value: categoryId, type: 'id' },
      {
        field: 'date',
        op: 'is',
        value: month,
        options: { month: true },
        type: 'date',
      },
    ];
    void navigate('/accounts', {
      state: {
        goBack: true,
        filterConditions,
        categoryId,
      },
    });
  }, [categoryId, month, navigate]);

  return (
    <Modal
      name="category-activity-preview"
      containerProps={{
        style: { width: 900, maxWidth: '90vw' },
      }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  gap: 8,
                }}
              >
                <Text style={{ fontWeight: 600 }}>{category?.name}</Text>
                <Text
                  style={{
                    color: theme.pageTextSubdued,
                    fontSize: 13,
                    textTransform: 'capitalize',
                  }}
                >
                  {formattedMonth}
                </Text>
              </View>
            }
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0 14px 0',
            }}
          >
            <View style={{ gap: 2 }}>
              <Text
                style={{
                  color: theme.pageTextSubdued,
                  ...styles.smallText,
                }}
              >
                {category?.is_income ? (
                  <Trans>Total received</Trans>
                ) : (
                  <Trans>Total spent</Trans>
                )}
              </Text>
              <FinancialText
                style={{
                  ...styles.veryLargeText,
                  ...styles.tnum,
                  fontWeight: 600,
                }}
              >
                {format(totalAmount, 'financial')}
              </FinancialText>
            </View>

            {hasSchedule && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  backgroundColor: theme.pillBackground,
                  borderRadius: 4,
                  color:
                    scheduleStatus === 'missed'
                      ? theme.budgetNumberNegative
                      : scheduleStatus === 'due'
                        ? theme.templateNumberUnderFunded
                        : theme.upcomingText,
                  ...styles.smallText,
                }}
                title={description}
              >
                <SvgArrowsSynchronize style={{ width: 13, height: 13 }} />
                <Text style={{ fontWeight: 500 }}>
                  {description || t('Upcoming schedule due this month')}
                </Text>
              </View>
            )}
          </View>

          <View
            style={{
              ...styles.tableContainer,
              height: 350,
              flex: 'unset',
            }}
          >
            <TableHeader>
              <Cell value={t('Date')} width={95} />
              <Cell value={t('Payee')} width="flex" />
              <Cell value={t('Account')} width={130} />
              <Cell value={t('Category')} width={150} />
              <Cell value={t('Notes')} width="flex" />
              <Cell value={t('Amount')} width={100} textAlign="right" />
            </TableHeader>

            <Table<TransactionEntity>
              items={transactions as TransactionEntity[]}
              getItemKey={index => transactions[index]?.id}
              style={{ backgroundColor: theme.tableHeaderBackground }}
              renderItem={({ item, index }) => {
                const isSplit = Boolean(item.is_child || item.parent_id);
                const payeeName = getPayeeName(item);
                const accountName = getAccountName(item);
                const isTransfer = Boolean(
                  item.transfer_id ||
                  (item.payee && payeesById[item.payee]?.transfer_acct),
                );
                const isEditable = !isSplit && !item.is_parent && !isTransfer;

                return (
                  <Row
                    key={item.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? theme.tableBackground
                          : theme.tableRowBackgroundAlternate,
                      color: theme.tableText,
                    }}
                  >
                    <Field width={95}>{formatTransactionDate(item.date)}</Field>
                    <Field width="flex" truncate={false} title={payeeName}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {isSplit && (
                          <SvgSplit
                            style={{
                              width: 12,
                              height: 12,
                              color: theme.pageTextSubdued,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Text
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {payeeName}
                        </Text>
                      </View>
                    </Field>
                    <Field width={130} title={accountName}>
                      {accountName}
                    </Field>
                    <Field width={150} title={category?.name}>
                      {isEditable ? (
                        <CategoryCell
                          categoryName={category?.name ?? ''}
                          menuItems={categoryMenuItems}
                          onSelect={newCategoryId =>
                            void onChangeCategory(item, newCategoryId)
                          }
                        />
                      ) : (
                        <Text>{category?.name}</Text>
                      )}
                    </Field>
                    <Field width="flex" title={item.notes || ''}>
                      {item.notes || ''}
                    </Field>
                    <Field
                      width={100}
                      style={{ textAlign: 'right' }}
                      contentStyle={{ alignItems: 'flex-end' }}
                    >
                      <FinancialText>
                        {format(item.amount, 'financial')}
                      </FinancialText>
                    </Field>
                  </Row>
                );
              }}
              renderEmpty={
                isTransactionsLoading ? (
                  <View style={{ padding: 30, alignItems: 'center' }}>
                    <AnimatedLoading style={{ width: 20, height: 20 }} />
                  </View>
                ) : (
                  <View
                    style={{
                      padding: 30,
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.pageTextSubdued,
                      fontStyle: 'italic',
                    }}
                  >
                    <Trans>
                      No transactions found for this category in this month.
                    </Trans>
                  </View>
                )
              }
            />
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 15,
            }}
          >
            <Text style={{ color: theme.pageTextSubdued, ...styles.smallText }}>
              <Trans count={transactions.length}>
                {{ count: transactions.length }} transactions
              </Trans>
            </Text>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <Button onPress={() => state.close()}>
                <Trans>Close</Trans>
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  state.close();
                  onOpenFullActivity();
                }}
              >
                <Trans>Open full view</Trans>
              </Button>
            </View>
          </View>
        </>
      )}
    </Modal>
  );
}
